import { useCallback, useEffect } from 'react';
import { $getRoot, COMMAND_PRIORITY_LOW, KEY_ENTER_COMMAND } from 'lexical';

const usePromptHandlers = ({ editorRef, onSubmit, onSetPrompt }) => {
  const handleSubmit = useCallback(
    (evt, text = null) => {
      evt.preventDefault();

      if (!text) {
        return;
      }

      const editor = editorRef?.current;

      editor.update(() => {
        const root = $getRoot();
        onSubmit(text);
        onSetPrompt('');
        root.clear();
      });
    },
    [onSubmit, editorRef]
  );

  const handleOnChange = useCallback(() => {
    const editor = editorRef.current;
    editor.update(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      onSetPrompt?.(textContent);
    });
  }, [editorRef, onSetPrompt]);

  const handleKeyDown = useCallback(
    (evt) => {
      const editor = editorRef.current;
      if (evt.key === 'Enter' && evt.shiftKey) {
        // Allow default behavior for Shift+Enter (creates newline)
        return false;
      }

      if (evt.key === 'Enter') {
        evt.stopPropagation();
        evt.preventDefault(); // Prevent the default behavior of adding a new line

        editor.update(() => {
          const root = $getRoot();
          const textContent = root.getTextContent();
          const trimmedTextContent = textContent.trim();

          if (trimmedTextContent === '') return;

          handleSubmit(evt, trimmedTextContent);
          root.clear();
        });

        return true;
      }

      return false;
    },
    [editorRef, handleSubmit]
  );

  useEffect(() => {
    const registerEnterCommand = () => {
      editorRef?.current.registerCommand(KEY_ENTER_COMMAND, handleKeyDown, COMMAND_PRIORITY_LOW);
    };
    registerEnterCommand();
  }, [editorRef, handleKeyDown]);

  return { handleSubmit, handleKeyDown, handleOnChange };
};

export { usePromptHandlers };
